import { ExternalLinkIcon } from '@va/icons';
import { Heading1, Heading2, Heading3, Heading4, Heading5, Paragraph } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import Markdown, { Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';

type MarkdownRendererProps = {
  content: string;
  remarkPlugins?: Options['remarkPlugins'];
};

export const MarkdownRenderer = ({ content, remarkPlugins }: MarkdownRendererProps) => {
  return (
    <Markdown
      // GFM plugin adds support for tables, task lists, and more.
      remarkPlugins={[remarkGfm, ...(remarkPlugins ?? [])]}
      components={{
        h1({ children, className }) {
          return (
            <Heading1 className={className} variant={'body'}>
              {children}
            </Heading1>
          );
        },
        h2({ children, className }) {
          return (
            <Heading2 className={className} variant={'body'}>
              {children}
            </Heading2>
          );
        },
        h3({ children, className }) {
          return (
            <Heading3 className={className} variant={'body'} variantSize={'small'}>
              {children}
            </Heading3>
          );
        },
        h4({ children, className }) {
          return (
            <Heading4 className={className} variant={'body'} variantSize={'small'}>
              {children}
            </Heading4>
          );
        },
        h5({ children, className }) {
          return (
            <Heading5 className={className} variant={'body'} variantSize={'small'}>
              {children}
            </Heading5>
          );
        },
        p({ children, className }) {
          return (
            <Paragraph className={className} variant={'body'} variantSize={'small'}>
              {children}
            </Paragraph>
          );
        },
        ul({ children, className }) {
          return <ul className={`${className ?? ''} list-disc pl-4`.trim()}>{children}</ul>;
        },
        ol({ children, className, start }) {
          return (
            <ol className={`${className ?? ''} list-decimal pl-4`.trim()} start={start}>
              {children}
            </ol>
          );
        },
        li({ children, className }) {
          return <li className={className}>{children}</li>;
        },
        strong({ children, className }) {
          return <strong className={`${className ?? ''} font-semibold`.trim()}>{children}</strong>;
        },
        em({ children, className }) {
          return <em className={className}>{children}</em>;
        },
        del({ children, className }) {
          return <del className={className}>{children}</del>;
        },
        input({ className, node: _node, ...props }) {
          return <input {...props} className={`${className ?? ''} mr-2u align-middle`.trim()} />;
        },
        table({ children, className }) {
          return (
            <div className='max-w-full overflow-x-auto scrollbar scrollbar-thumb'>
              <table className={`${className ?? ''} w-full border-collapse text-left`.trim()}>{children}</table>
            </div>
          );
        },
        thead({ children, className }) {
          return <thead className={`${className ?? ''} bg-white-wild-sand`.trim()}>{children}</thead>;
        },
        th({ children, className, style }) {
          return (
            <th
              className={`${className ?? ''} border border-gray-mercury px-4u py-2u font-semibold`.trim()}
              style={style}
            >
              {children}
            </th>
          );
        },
        td({ children, className, style }) {
          return (
            <td className={`${className ?? ''} border border-gray-mercury px-4u py-2u align-top`.trim()} style={style}>
              {children}
            </td>
          );
        },
        a({ children, className, href }) {
          return (
            <TooltipWrapper content={href}>
              <a
                className={`${className ?? ''} font-semibold text-primary underline`.trim()}
                href={href}
                target='_blank'
                rel='noopener noreferrer'
              >
                {children}
                <ExternalLinkIcon className='ml-1 inline-block h-3 w-3 align-[-0.125em]' color='currentColor' />
              </a>
            </TooltipWrapper>
          );
        },
      }}
    >
      {content}
    </Markdown>
  );
};
